<template>
  <div>
    <v-row align="end">
      <v-col cols="4" md="2" class="d-flex align-end">
        <v-select
          v-model="filtros.agrupacion"
          :items="tiposAgrupacion"
          item-value="value"
          item-text="text"
          label="Tipo de Agrupación"
          :disabled="loadingReporte"
          class="mb-0"
          hide-details
          @change="onTipoAgrupacionChange"
        />
      </v-col>

      <v-col v-if="filtros.agrupacion !== 2" cols="4" md="2" class="d-flex align-end">
        <VueCtkDateTimePicker
          v-model="filtros.Desde"
          format="YYYY-MM-DD HH:mm"
          formatted="YYYY-MM-DD HH:mm"
          :max-date="filtros.Hasta"
          label="Desde (Obligatorio)"
          noHeader
          overlay
          buttonNowTranslation="Ahora"
          :disabled="loadingReporte"
        />
      </v-col>

      <v-col v-if="filtros.agrupacion !== 2" cols="4" md="2" class="d-flex align-end">
        <VueCtkDateTimePicker
          v-model="filtros.Hasta"
          format="YYYY-MM-DD HH:mm"
          formatted="YYYY-MM-DD HH:mm"
          :min-date="filtros.Desde"
          label="Hasta (Obligatorio)"
          noHeader
          overlay
          buttonNowTranslation="Ahora"
          :disabled="loadingReporte"
        />
      </v-col>

      <v-col v-if="filtros.agrupacion === 2" cols="4" md="2" class="d-flex align-end">
        <week-picker-calendar
          v-model="semanas"
          class="productividad-week-picker"
          @apply="onAplicar"
        />
      </v-col>

      <v-col cols="4" md="2" class="d-flex align-end">
        <v-autocomplete
          v-model="filtros.Areas"
          :items="areas"
          item-text="nombre"
          item-value="idArea"
          multiple
          chips
          clearable
          label="Área"
          :loading="loadingAreas"
          :disabled="loadingReporte"
          class="mb-0"
          hide-details
        >
          <template v-slot:selection="{ index }">
            <v-chip v-if="index === 0" small>
              {{ filtros.Areas.length }} áreas
            </v-chip>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="4" md="2" class="d-flex align-end">
        <v-autocomplete
          v-model="filtros.Maquinas"
          :items="maquinas"
          :item-text="nombreMaquina"
          item-value="idMaquinaIns"
          multiple
          chips
          clearable
          label="Línea"
          :loading="loadingMaquinas"
          :disabled="loadingReporte"
          class="mb-0"
          hide-details
        >
          <template v-slot:selection="{ index }">
            <v-chip v-if="index === 0" small>
              {{ filtros.Maquinas.length }} máquinas
            </v-chip>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="4" md="2" class="d-flex align-end">
        <v-autocomplete
          v-model="filtros.Turnos"
          :items="turnos"
          item-text="nombre"
          item-value="idTurno"
          multiple
          chips
          clearable
          label="Turno"
          :loading="loadingTurnos"
          :disabled="loadingReporte"
          class="mb-0"
          hide-details
        >
          <template v-slot:selection="{ index }">
            <v-chip v-if="index === 0" small>
              {{ filtros.Turnos.length }} turnos
            </v-chip>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="4" md="2" class="d-flex align-end">
        <v-autocomplete
          v-model="filtros.Operadores"
          :items="operadores"
          :item-text="nombrePersona"
          item-value="idPersona"
          multiple
          chips
          clearable
          label="Operador"
          :loading="loadingOperadores"
          :disabled="loadingReporte"
          class="mb-0"
          hide-details
        >
          <template v-slot:selection="{ index }">
            <v-chip v-if="index === 0" small>
              {{ filtros.Operadores.length }} personas
            </v-chip>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>

    <div class="d-flex align-center">
      <v-spacer />
      <v-btn color="primary" :loading="loadingReporte" :disabled="!filtrosValidos" @click="consultarReporte">
        Consultar
      </v-btn>
      <v-btn
        color="success"
        class="ml-2"
        :loading="loadingExcel"
        :disabled="loadingReporte || loadingExcel || !filtrosValidos"
        @click="descargarExcel"
      >
        Descargar Excel
      </v-btn>
    </div>

    <LoaderData :loading="loadingReporte" :response="tablaProductividad" :error="errorReporte">
      <section class="productividad-kpi-grid">
        <article
          v-for="card in kpiProductividad"
          :key="card.key"
          class="productividad-kpi-card"
          :class="`productividad-kpi-card--${card.tone}`"
        >
          <div class="productividad-kpi-top">
            <div class="productividad-kpi-icon-pack" :class="`productividad-kpi-icon-pack--${card.tone}`">
              <v-icon class="productividad-kpi-icon">{{ card.icon }}</v-icon>
            </div>
            <p class="productividad-kpi-title">{{ card.title }}</p>
          </div>
          <p v-if="card.value" class="productividad-kpi-value">{{ card.value }}</p>
          <p v-if="card.caption" class="productividad-kpi-caption">{{ card.caption }}</p>
        </article>
      </section>

      <v-data-table
        :headers="headersProductividadFinal"
        :items="tablaProductividad"
        class="mt-4"
        dense
      >
        <template v-slot:[`item.area`]="{ item }">
          {{ item.area || item.Area || item.nombreArea || item.NombreArea || '' }}
        </template>
        <template v-slot:[`item.operador`]="{ item }">
          {{ item.operador || item.Operador || item.nombreOperador || item.NombreOperador || '' }}
        </template>
        <template v-slot:[`item.disponibilidad`]="{ item }">
          {{ formatearPorcentaje(valorPorCampos(item, ['disponibilidad', 'Disponibilidad'])) }}
        </template>
        <template v-slot:[`item.productividad`]="{ item }">
          {{ formatearPorcentaje(valorPorCampos(item, ['productividad', 'Productividad'])) }}
        </template>
        <template v-slot:[`item.calidad`]="{ item }">
          {{ formatearPorcentaje(valorPorCampos(item, ['calidad', 'Calidad'])) }}
        </template>
        <template v-slot:[`item.ope`]="{ item }">
          {{ formatearPorcentaje(valorPorCampos(item, ['ope', 'Ope', 'OPE'])) }}
        </template>
        <template v-slot:[`item.tiempoTrabajadoSegundos`]="{ item }">
          {{ formatearSegundosAHoras(valorPorCampos(item, ['tiempoTrabajadoSegundos', 'TiempoTrabajadoSegundos'])) }}
        </template>
      </v-data-table>
    </LoaderData>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import LoaderData from '../../LoaderData.vue';
import WeekPickerCalendar from '../../WeekPickerCalendar.vue';

export default {
  components: {
    LoaderData,
    WeekPickerCalendar,
  },
  data() {
    return {
      headersProductividad: [
        { text: 'Periodo', value: 'periodo', align: "center", sortable: false },
        { text: 'ODT', value: 'clave', align: "center", sortable: false },
        { text: 'Línea', value: 'maquina', align: "center", sortable: false },
        { text: 'Operador', value: 'operador', align: "center", sortable: false },
        { text: 'Área', value: 'area', align: "center", sortable: false },
        { text: 'Planeado ODT', value: 'cantidadPlaneadaOdt', align: "center", sortable: false },
        { text: 'Piezas Esperadas', value: 'piezasEsperadasTiempoTrabajado', align: "center", sortable: false },
        { text: 'OK', value: 'cantidadOk', align: "center", sortable: false },
        { text: 'Scrap', value: 'cantidadScrap', align: "center", sortable: false },
        { text: 'Total', value: 'cantidadTotal', align: "center", sortable: false },
        { text: 'TC Operador (s)', value: 'tcOperadorSegundos', align: "center", sortable: false },
        { text: 'Tiempo Trabajado (s)', value: 'tiempoTrabajadoSegundos', align: "center", sortable: false },
        { text: 'Disponibilidad', value: 'disponibilidad', align: "center", sortable: false },
        { text: 'Productividad', value: 'productividad', align: "center", sortable: false },
        { text: 'Calidad', value: 'calidad', align: "center", sortable: false },
        { text: 'OPE', value: 'ope', align: "center", sortable: false },
      ],
      tiposAgrupacion: [
        { text: 'Días', value: 1 },
        { text: 'Semanas', value: 2 },
        { text: 'Mes', value: 3 },
      ],
      tablaProductividad: [],
      resumenProductividad: {},
      semanas: [],
      maquinas: [],
      areas: [],
      turnos: [],
      operadores: [],
      filtros: {
        agrupacion: 1,
        Desde: null,
        Hasta: null,
        semanasSeleccionadas: [],
        Maquinas: [],
        Areas: [],
        Turnos: [],
        Operadores: [],
      },
      loadingReporte: false,
      loadingMaquinas: false,
      loadingAreas: false,
      loadingTurnos: false,
      loadingOperadores: false,
      loadingExcel: false,
      errorReporte: false,
      errores: [],
    };
  },
  computed: {
    headersProductividadFinal() {
      return this.headersProductividad.map(header => {
        if (header.value !== 'periodo') return header;

        const textos = {
          1: 'Día',
          2: 'Semana',
          3: 'Mes',
        };

        return {
          ...header,
          text: textos[this.filtros.agrupacion] || 'Día',
        };
      });
    },
    kpiProductividad() {
      return [
        {
          key: 'promedio-general',
          title: 'OPE Global',
          value: this.promedioOpe,
          caption: `${this.tablaProductividad.length} registros`,
          icon: 'mdi-chart-line',
          tone: 'blue',
        },
        {
          key: 'disponibilidad-general',
          title: 'Disponibilidad Global',
          value: this.promedioDisponibilidad,
          caption: 'Promedio del periodo',
          icon: 'mdi-clock-check-outline',
          tone: 'green',
        },
        {
          key: 'productividad-general',
          title: 'Productividad Global',
          value: this.promedioProductividad,
          caption: 'Promedio del periodo',
          icon: 'mdi-factory',
          tone: 'amber',
        },
        {
          key: 'calidad-general',
          title: 'Calidad Global',
          value: this.promedioCalidad,
          caption: 'Promedio del periodo',
          icon: 'mdi-check-decagram-outline',
          tone: 'green',
        },
      ];
    },
    promedioOpe() {
      return this.formatearPorcentaje(this.valorPorCampos(this.resumenProductividad, ['opeGlobal', 'OpeGlobal', 'OPEGlobal']));
    },
    promedioDisponibilidad() {
      return this.formatearPorcentaje(this.valorPorCampos(this.resumenProductividad, ['disponibilidadGlobal', 'DisponibilidadGlobal']));
    },
    promedioProductividad() {
      return this.formatearPorcentaje(this.valorPorCampos(this.resumenProductividad, ['productividadGlobal', 'ProductividadGlobal']));
    },
    promedioCalidad() {
      return this.formatearPorcentaje(this.valorPorCampos(this.resumenProductividad, ['calidadGlobal', 'CalidadGlobal']));
    },
    filtrosValidos() {
      const desde = this.parseFechaFiltro(this.filtros.Desde);
      const hasta = this.parseFechaFiltro(this.filtros.Hasta);

      const fechasValidas = this.filtros.agrupacion === 2
        ? this.filtros.semanasSeleccionadas.length > 0
        : desde.isValid() &&
          hasta.isValid() &&
          desde.isSameOrBefore(hasta);

      return (
        fechasValidas &&
        [1, 2, 3].includes(this.filtros.agrupacion)
      );
    },
  },
  mounted() {
    this.getMaquinas();
    this.getAreas();
    this.getTurnos();
    this.getOperadores();
  },
  methods: {
    nombreMaquina(maquina) {
      if (!maquina) return '';
      const nombre = maquina.nombre || maquina.Nombre || '';
      const marca = maquina.marca || maquina.Marca || '';
      const modelo = maquina.modelo || maquina.Modelo || '';

      return `[${nombre}] - ${marca} ${modelo}`.trim();
    },
    nombrePersona(persona) {
      if (!persona) return '';
      const { nombre, paterno, materno } = persona;

      return `${nombre ? `${nombre} ` : ''}${paterno ? `${paterno} ` : ''}${materno ? `${materno} ` : ''}`.trim();
    },
    parsePorcentaje(valor) {
      if (valor === null || valor === undefined || valor === '') return null;
      const numero = Number(String(valor).replace('%', '').trim());
      return Number.isFinite(numero) ? numero : null;
    },
    promedioCampo(campos) {
      const valores = this.tablaProductividad
        .map(item => this.parsePorcentaje(this.valorPorCampos(item, campos)))
        .filter(valor => valor !== null);

      if (!valores.length) return '0%';

      const promedio = valores.reduce((total, valor) => total + valor, 0) / valores.length;
      return `${promedio.toFixed(2)}%`;
    },
    valorPorCampos(item, campos) {
      const listaCampos = Array.isArray(campos) ? campos : [campos];
      const campo = listaCampos.find(nombre => item[nombre] !== null && item[nombre] !== undefined && item[nombre] !== '');
      return campo ? item[campo] : null;
    },
    formatearPorcentaje(valor) {
      const numero = this.parsePorcentaje(valor);
      if (numero === null) return '';
      return `${numero.toFixed(2)}%`;
    },
    formatearSegundosAHoras(valor) {
      const segundos = Number(valor);
      if (!Number.isFinite(segundos)) return '';

      const totalMinutos = Math.floor(segundos / 60);
      const horas = Math.floor(totalMinutos / 60);
      const minutos = totalMinutos % 60;

      return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}`;
    },
    parseFechaFiltro(fecha) {
      return moment(String(fecha || '').trim(), 'YYYY-MM-DD HH:mm', true);
    },
    consultarReporte() {
      this.errorReporte = false;
      this.errores = [];
      this.loadingReporte = true;

      const params = this.crearParamsReporteOpe();

      axios
        .get('/OPE/ReporteOpePersonal', { params })
        .then(response => {
          const resumen = this.obtenerResumenReporte(response.data);
          const filas = this.obtenerFilasReporte(response.data);

          this.resumenProductividad = resumen;
          this.tablaProductividad = filas;
          this.loadingReporte = false;
        })
        .catch(error => {
          console.error('Error al consultar reporte OPE:', error);
          this.loadingReporte = false;
          this.errorReporte = true;
          this.tablaProductividad = [];
          this.resumenProductividad = {};
          this.errores = ['Error al obtener los datos. Por favor, intente nuevamente.'];

          if (error.response && error.response.status === 404) {
            this.errores = ['No se encontraron datos para el periodo seleccionado.'];
          }

          this.$toast?.error?.(this.errores[0]);
        });
    },
    descargarExcel() {
      this.loadingExcel = true;

      const params = this.crearParamsReporteOpe();

      axios
        .get(`/OPE/ReporteOpePersonalExcel?${params.toString()}`, { responseType: 'blob' })
        .then(response => {
          const blob = new Blob(
            [response.data],
            { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
          );
          const link = document.createElement('a');
          const blobURL = URL.createObjectURL(blob);

          link.download = `reporte-ope-personal-${moment().format('YYYYMMDDHHmmss')}.xlsx`;
          link.href = blobURL;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobURL);
        })
        .catch(error => {
          console.error('Error al descargar reporte OPE:', error);
          this.$toast?.error?.('No se pudo descargar el reporte OPE');
        })
        .finally(() => {
          this.loadingExcel = false;
        });
    },
    crearParamsReporteOpe() {
      const params = new URLSearchParams();

      params.append('fechaInicio', this.filtros.Desde || '');
      params.append('fechaFin', this.filtros.Hasta || '');
      params.append('agrupacion', this.filtros.agrupacion);

      this.filtros.Operadores.forEach(id => params.append('idPersona', id));
      this.filtros.Areas.forEach(id => params.append('idArea', id));
      this.filtros.Maquinas.forEach(id => params.append('idMaquinaIns', id));
      this.filtros.Turnos.forEach(id => params.append('idTurno', id));

      return params;
    },
    obtenerResumenReporte(data) {
      if (data?.data && !Array.isArray(data.data)) return data.data;
      if (data?.result && !Array.isArray(data.result)) return data.result;
      return data || {};
    },
    obtenerFilasReporte(data) {
      if (Array.isArray(data)) {
        return data.flatMap(item => this.obtenerFilasDeGrupo(item));
      }

      if (Array.isArray(data?.registros)) return this.obtenerFilasReporte(data.registros);
      if (Array.isArray(data?.Registros)) return this.obtenerFilasReporte(data.Registros);
      if (Array.isArray(data?.data?.registros)) return this.obtenerFilasReporte(data.data.registros);
      if (Array.isArray(data?.data?.Registros)) return this.obtenerFilasReporte(data.data.Registros);
      if (Array.isArray(data?.result?.registros)) return this.obtenerFilasReporte(data.result.registros);
      if (Array.isArray(data?.result?.Registros)) return this.obtenerFilasReporte(data.result.Registros);
      if (Array.isArray(data?.detalles)) return this.obtenerFilasDeGrupo(data);
      if (Array.isArray(data?.data?.detalles)) return this.obtenerFilasDeGrupo(data.data);
      if (Array.isArray(data?.data)) return this.obtenerFilasReporte(data.data);
      if (Array.isArray(data?.result)) return this.obtenerFilasReporte(data.result);

      return [];
    },
    obtenerFilasDeGrupo(grupo) {
      if (!Array.isArray(grupo?.detalles)) return [grupo];

      const { detalles, ...datosGrupo } = grupo;
      if (this.filtros.agrupacion === 2) {
        return [{
          ...datosGrupo,
          area: this.valoresUnicosDetalles(detalles, ['area', 'Area', 'nombreArea', 'NombreArea']),
          clave: this.valoresUnicosDetalles(detalles, ['clave', 'Clave']),
          maquina: this.valoresUnicosDetalles(detalles, ['maquina', 'Maquina', 'linea', 'Linea', 'nombreMaquina', 'NombreMaquina']),
        }];
      }

      return detalles.map(detalle => ({
        ...datosGrupo,
        ...detalle,
      }));
    },
    valoresUnicosDetalles(detalles, campos) {
      const valores = detalles
        .map(detalle => this.valorPorCampos(detalle, campos))
        .filter(valor => valor !== null && valor !== undefined && valor !== '');

      return [...new Set(valores)].join(', ');
    },
    onTipoAgrupacionChange() {
      this.filtros.Desde = null;
      this.filtros.Hasta = null;
      this.filtros.semanasSeleccionadas = [];
      this.semanas = [];
    },
    onAplicar(weeks) {
      const lista = weeks || this.semanas || [];
      this.setRangeFromWeeks(lista);
      this.filtros.semanasSeleccionadas = lista.map(w => ({
        year: w.isoYear,
        week: w.isoWeek,
        desde: w.start,
        hasta: w.end,
        etiqueta: `Sem. ${String(w.isoWeek).padStart(2, '0')} '${String(w.isoYear).slice(-2)}`,
      }));
    },
    setRangeFromWeeks(weeks) {
      if (!weeks || !weeks.length) {
        this.filtros.Desde = null;
        this.filtros.Hasta = null;
        return;
      }

      const starts = weeks.map(w => w.start).sort();
      const ends = weeks.map(w => w.end).sort();
      this.filtros.Desde = `${starts[0]} 00:00`;
      this.filtros.Hasta = `${ends[ends.length - 1]} 23:59`;
    },
    getMaquinas() {
      this.loadingMaquinas = true;
      axios
        .get('/MaquinaInstancias')
        .then(res => {
          this.maquinas = res.data;
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          this.loadingMaquinas = false;
        });
    },
    getAreas() {
      this.loadingAreas = true;
      axios
        .get('/Areas')
        .then(res => {
          this.areas = res.data.filter(area => area.idArea > 1);
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          this.loadingAreas = false;
        });
    },
    getTurnos() {
      this.loadingTurnos = true;
      axios
        .get('/Turnos')
        .then(res => {
          this.turnos = res.data;
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          this.loadingTurnos = false;
        });
    },
    getOperadores() {
      this.loadingOperadores = true;
      axios
        .get('/Personas/Operadores')
        .then(res => {
          this.operadores = res.data;
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          this.loadingOperadores = false;
        });
    },
  },
};
</script>

<style scoped>
.productividad-week-picker {
  width: 100%;
}

.productividad-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.productividad-kpi-card {
  border: 1px solid #e6e8ee;
  border-radius: 8px;
  background: #ffffff;
  padding: 14px 16px;
  min-height: 112px;
}

.productividad-kpi-card--blue {
  border-left: 4px solid #2a6099;
}

.productividad-kpi-card--green {
  border-left: 4px solid #2f855a;
}

.productividad-kpi-card--amber {
  border-left: 4px solid #b7791f;
}

.productividad-kpi-card--violet {
  border-left: 4px solid #6b46c1;
}

.productividad-kpi-top {
  display: flex;
  align-items: center;
  gap: 10px;
}

.productividad-kpi-icon-pack {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.productividad-kpi-icon-pack--blue {
  background: #e8f1fb;
}

.productividad-kpi-icon-pack--green {
  background: #e7f6ee;
}

.productividad-kpi-icon-pack--amber {
  background: #fff4dc;
}

.productividad-kpi-icon-pack--violet {
  background: #f1ecff;
}

.productividad-kpi-icon {
  font-size: 20px;
}

.productividad-kpi-icon-pack--blue .productividad-kpi-icon {
  color: #2a6099;
}

.productividad-kpi-icon-pack--green .productividad-kpi-icon {
  color: #2f855a;
}

.productividad-kpi-icon-pack--amber .productividad-kpi-icon {
  color: #b7791f;
}

.productividad-kpi-icon-pack--violet .productividad-kpi-icon {
  color: #6b46c1;
}

.productividad-kpi-title {
  color: #5f6875;
  font-size: 13px;
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
}

.productividad-kpi-value {
  color: #1f2937;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.1;
  margin: 12px 0 0;
}

.productividad-kpi-caption {
  color: #748093;
  font-size: 13px;
  margin: 6px 0 0;
}

@media (max-width: 960px) {
  .productividad-kpi-grid {
    grid-template-columns: 1fr;
  }
}
</style>
